import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from 'react';
import AdminLayout from '../../Components/Admin/AdminLayout';
import { Container, Row, Col, Card, CardHeader, CardBody, Alert, Spinner, Progress, Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faChartPie, faDollarSign, faSlidersH, faArrowUp } from '@fortawesome/free-solid-svg-icons';
const API_BASE = window.HMI_API_BASE || process.env.REACT_APP_API_URL || 'https://api.heresmyinfo.com/api';
export default function Reports() {
  const {
    t
  } = useTranslation();
  const [reportsData, setReportsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const token = localStorage.getItem('token') || localStorage.getItem('accessToken') || '';

  const fetchReports = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/admin/pricing-reports`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();

      if (data.success) {
        setReportsData(data.data);
      } else {
        setError(data.error?.message || 'Failed to fetch reports data');
      }
    } catch (err) {
      console.error('Error fetching reports:', err);
      setError('Connection error loading reports dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPlanColor = type => {
    switch (type?.toLowerCase()) {
      case 'individual':
        return 'info';

      case 'business':
        return 'success';

      case 'custom':
        return 'warning';

      default:
        return 'primary';
    }
  };

  const calculatePercentage = (count, total) => {
    if (!total || total === 0) return 0;
    return Math.round(parseInt(count, 10) / total * 100);
  };

  return <AdminLayout>
      <Container fluid className="px-4">
        <Row className="mb-4 align-items-center">
          <Col md={8}>
            <h2>{t("Billing & Subscriptions Reports")}</h2>
            <p className="text-muted">{t("Analyze your Monthly Recurring Revenue (MRR) and plan statistics.")}</p>
          </Col>
          <Col md={4} className="text-right">
            <Button color="primary" onClick={fetchReports} disabled={loading}>
              <FontAwesomeIcon icon={faSync} className={`mr-2 ${loading ? 'fa-spin' : ''}`} />{t("Refresh")}</Button>
          </Col>
        </Row>

        {error && <Alert color="danger">{error}</Alert>}

        {loading ? <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 text-muted">{t("Calculating revenue and distribution metrics...")}</p>
          </div> : reportsData ? <>
            {
          /* Top Stat Cards */
        }
            <Row className="mb-4">
              <Col md={4} className="mb-3">
                <Card className="text-white bg-success h-100 border-0 shadow-sm">
                  <CardBody className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="text-uppercase text-white-50 small font-weight-bold">{t("Estimated MRR")}</div>
                      <h2 className="mb-0 mt-1 font-weight-bold" style={{
                    fontSize: '2.2rem'
                  }}>
                        ${parseFloat(reportsData.estimatedMRR || 0).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                      </h2>
                      <div className="small mt-2">
                        <FontAwesomeIcon icon={faArrowUp} className="mr-1" />
                        <span>{t("Active monthly + pro-rated yearly + add-ons")}</span>
                      </div>
                    </div>
                    <FontAwesomeIcon icon={faDollarSign} size="3x" className="text-white-50" />
                  </CardBody>
                </Card>
              </Col>
              
              <Col md={4} className="mb-3">
                <Card className=" h-100 border-0 shadow-sm">
                  <CardBody className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="text-uppercase text-muted small font-weight-bold">{t("Active Subscriptions")}</div>
                      <h2 className="mb-0 mt-1 font-weight-bold text-light" style={{
                    fontSize: '2.2rem'
                  }}>
                        {reportsData.planDistribution?.reduce((acc, curr) => acc + parseInt(curr.count, 10), 0) || 0}
                      </h2>
                      <div className="small text-muted mt-2">{t("Total active customer relations")}</div>
                    </div>
                    <FontAwesomeIcon icon={faChartPie} size="3x" className="text-muted" style={{
                  opacity: 0.3
                }} />
                  </CardBody>
                </Card>
              </Col>

              <Col md={4} className="mb-3">
                <Card className=" h-100 border-0 shadow-sm">
                  <CardBody className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="text-uppercase text-muted small font-weight-bold">{t("Active Add-on Units")}</div>
                      <h2 className="mb-0 mt-1 font-weight-bold text-light" style={{
                    fontSize: '2.2rem'
                  }}>
                        {reportsData.addonDistribution?.reduce((acc, curr) => acc + parseInt(curr.count, 10), 0) || 0}
                      </h2>
                      <div className="small text-muted mt-2">{t("Total \xE0 la carte items assigned")}</div>
                    </div>
                    <FontAwesomeIcon icon={faSlidersH} size="3x" className="text-muted" style={{
                  opacity: 0.3
                }} />
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {
          /* Distribution Charts */
        }
            <Row>
              <Col lg={6} className="mb-4">
                <Card className="h-100 border-0 shadow-sm">
                  <CardHeader className=" border-0 py-3">
                    <h5 className="mb-0 font-weight-bold text-light">{t("Plan Level Distribution")}</h5>
                  </CardHeader>
                  <CardBody>
                    {reportsData.planDistribution && reportsData.planDistribution.length > 0 ? (() => {
                  const total = reportsData.planDistribution.reduce((acc, curr) => acc + parseInt(curr.count, 10), 0);
                  return <div className="d-flex flex-column gap-3">
                            {reportsData.planDistribution.map((plan, i) => {
                      const pct = calculatePercentage(plan.count, total);
                      return <div key={i} className="mb-3">
                                  <div className="d-flex justify-content-between align-items-center mb-1">
                                    <span>
                                      <strong>{plan.name}</strong>{' '}
                                      <small className="text-muted text-uppercase">({plan.type})</small>
                                    </span>
                                    <span className="font-weight-bold text-light">
                                      {plan.count} ({pct}%)
                                    </span>
                                  </div>
                                  <Progress value={pct} color={getPlanColor(plan.type)} style={{
                          height: '10px',
                          borderRadius: '5px'
                        }} />
                                </div>;
                    })}
                          </div>;
                })() : <div className="text-center py-4 text-muted">{t("No plans distribution data.")}</div>}
                  </CardBody>
                </Card>
              </Col>

              <Col lg={6} className="mb-4">
                <Card className="h-100 border-0 shadow-sm">
                  <CardHeader className=" border-0 py-3">
                    <h5 className="mb-0 font-weight-bold text-light">{t("\xC0 La Carte Service Popularity")}</h5>
                  </CardHeader>
                  <CardBody>
                    {reportsData.addonDistribution && reportsData.addonDistribution.length > 0 ? (() => {
                  const total = reportsData.addonDistribution.reduce((acc, curr) => acc + parseInt(curr.count, 10), 0);
                  return <div className="d-flex flex-column gap-3">
                            {reportsData.addonDistribution.map((addon, i) => {
                      const pct = calculatePercentage(addon.count, total);
                      return <div key={i} className="mb-3">
                                  <div className="d-flex justify-content-between align-items-center mb-1">
                                    <strong>{addon.name}</strong>
                                    <span className="font-weight-bold text-light">
                                      {addon.count}{t("units (")}{pct}%)
                                    </span>
                                  </div>
                                  <Progress value={pct} color="info" style={{
                          height: '10px',
                          borderRadius: '5px'
                        }} />
                                </div>;
                    })}
                          </div>;
                })() : <div className="text-center py-4 text-muted">{t("No add-on services distribution data.")}</div>}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </> : <div className="text-center py-5">{t("No reports data available.")}</div>}
      </Container>
    </AdminLayout>;
}